import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../services/api';
import './ProfilePhoto.css';

interface ProfilePhotoProps {
  className?: string;
}

const ProfilePhoto: React.FC<ProfilePhotoProps> = ({ className = '' }) => {
  const { user, refreshUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    setError('');
    setSuccess('');
    
    // Validar archivo
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      setError('La imagen no puede ser mayor a 5MB');
      return;
    }

    setIsUploading(true);

    try {
      await usersAPI.uploadPhoto(file);
      setSuccess('Foto de perfil actualizada correctamente');
      
      // Actualizar datos del usuario sin recargar la página
      await refreshUser();
      
      // Limpiar mensaje de éxito después de un tiempo
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al subir la foto');
    } finally {
      setIsUploading(false);
      // Limpiar el input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    const confirmMessage = isGoogleUser && user?.avatar_url 
      ? '¿Quieres volver a usar tu foto de Google?'
      : '¿Estás seguro de que quieres eliminar tu foto de perfil?';
      
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setError('');
    setSuccess('');
    setIsDeleting(true);

    try {
      await usersAPI.deletePhoto();
      const successMessage = isGoogleUser && user?.avatar_url
        ? 'Ahora usando tu foto de Google'
        : 'Foto de perfil eliminada correctamente';
      setSuccess(successMessage);
      
      // Actualizar datos del usuario sin recargar la página
      await refreshUser();
      
      // Limpiar mensaje de éxito después de un tiempo
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar la foto');
    } finally {
      setIsDeleting(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getPhotoUrl = () => {
    // Para usuarios de Google, priorizar avatar_url (foto de Google) si no tienen profile_photo personalizada
    if (user?.auth_provider === 'google' || user?.auth_provider === 'both') {
      // Si tienen foto personalizada subida, usar esa
      if (user?.profile_photo) {
        if (user.profile_photo.startsWith('http')) {
          return user.profile_photo;
        }
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || window.location.origin;
        const photoPath = user.profile_photo.startsWith('/') ? user.profile_photo : `/${user.profile_photo}`;
        return `${baseUrl}${photoPath}`;
      }
      // Si no, usar la foto de Google
      if (user?.avatar_url) {
        return user.avatar_url;
      }
    } else {
      // Para usuarios locales, usar profile_photo si existe
      if (user?.profile_photo) {
        if (user.profile_photo.startsWith('http')) {
          return user.profile_photo;
        }
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || window.location.origin;
        const photoPath = user.profile_photo.startsWith('/') ? user.profile_photo : `/${user.profile_photo}`;
        return `${baseUrl}${photoPath}`;
      }
    }
    
    return null;
  };

  const isGoogleUser = user?.auth_provider === 'google' || user?.auth_provider === 'both';
  const hasGooglePhoto = user?.avatar_url && !user?.profile_photo;
  const hasCustomPhoto = !!user?.profile_photo;

  const photoUrl = getPhotoUrl();
  const hasPhoto = !!photoUrl;

  return (
    <div className={`profile-photo-container ${className}`}>
      <div className="profile-photo-section">
        <div className="photo-display">
          {hasPhoto ? (
            <img 
              src={photoUrl} 
              alt="Foto de perfil" 
              className="profile-photo-image"
              onError={(e) => {
                // Si la imagen falla al cargar, mostrar avatar por defecto
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          
          <div className={`profile-photo-placeholder ${hasPhoto ? 'hidden' : ''}`}>
            <div className="placeholder-icon">
              {user?.name ? user.name.charAt(0).toUpperCase() : '👤'}
            </div>
          </div>

          {hasPhoto && (
            <div className="photo-overlay">
              <button 
                onClick={triggerFileInput}
                className="overlay-button change-button"
                disabled={isUploading || isDeleting}
                title="Cambiar foto"
              >
                📷
              </button>
              <button 
                onClick={handleDelete}
                className="overlay-button delete-button"
                disabled={isUploading || isDeleting}
                title="Eliminar foto"
              >
                🗑️
              </button>
            </div>
          )}
        </div>

        <div className="photo-actions">
          {!hasPhoto && (
            <button
              onClick={triggerFileInput}
              className="btn-primary upload-button"
              disabled={isUploading || isDeleting}
            >
              {isUploading ? '📤 Subiendo...' : '📷 Subir Foto'}
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden-file-input"
          disabled={isUploading || isDeleting}
        />

        {error && (
          <div className="photo-message error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="photo-message success-message">
            {success}
          </div>
        )}

        <div className="photo-info">
          {isGoogleUser && hasGooglePhoto ? (
            <div className="google-photo-info">
              <p className="photo-help">
                📸 Usando tu foto de Google. Puedes subir una foto personalizada si lo deseas.
              </p>
            </div>
          ) : (
            <p className="photo-help">
              Sube una imagen (máx. 5MB) en formato JPG, PNG o GIF
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePhoto;